import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gesundheitApi, empfehlungenApi } from '../api';

const STATUS_COLOR = { excellent: '#22c55e', good: '#84cc16', fair: '#f59e0b', critical: '#f97316', urgent: '#ef4444' };
const STATUS_LABEL = { excellent: 'Ausgezeichnet', good: 'Gut', fair: 'Mäßig', critical: 'Kritisch', urgent: 'Dringend' };

const DIM_LABELS = [
  { key: 'data_quality_score', label: 'Datenqualität', max: 20 },
  { key: 'deadline_score', label: 'Fristen', max: 20 },
  { key: 'document_score', label: 'Dokumente', max: 20 },
  { key: 'cost_score', label: 'Kosten', max: 15 },
  { key: 'cancellation_score', label: 'Kündigung', max: 15 },
  { key: 'risk_score', label: 'Risiken', max: 10 },
  { key: 'history_score', label: 'Historie', max: 10 },
];

export default function VertragsGesundheitPage() {
  const [overview, setOverview] = useState(null);
  const [empfehlungen, setEmpfehlungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recalcLoading, setRecalcLoading] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, empRes] = await Promise.all([
        gesundheitApi.uebersicht(),
        empfehlungenApi.list({ status: 'new', limit: 10 })
      ]);
      setOverview(ovRes.data);
      setEmpfehlungen(Array.isArray(empRes.data) ? empRes.data : []);
    } catch {
      setError('Gesundheitsdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRecalc = async (contractId) => {
    setRecalcLoading(contractId);
    try {
      await gesundheitApi.berechnen(contractId);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Berechnung fehlgeschlagen');
    } finally {
      setRecalcLoading(null);
    }
  };

  const handleEmpfehlung = async (id, action) => {
    try {
      if (action === 'annehmen') await empfehlungenApi.annehmen(id);
      else if (action === 'ablehnen') await empfehlungenApi.ablehnen(id);
      setEmpfehlungen(e => e.filter(r => r.id !== id));
    } catch (e) {
      setError(e?.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Lädt Gesundheitsdaten…</div>;

  const summary = overview?.summary || {};
  const scores = overview?.scores || [];

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Vertragsgesundheit</h1>

      {error && <div style={{ background: '#fef2f2', color: '#ef4444', padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Zusammenfassung */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <SummaryCard label="Durchschnitt" value={`${summary.avg_score || 0}%`} color="#3b82f6" />
        <SummaryCard label="Ausgezeichnet" value={summary.excellent || 0} color={STATUS_COLOR.excellent} />
        <SummaryCard label="Gut" value={summary.good || 0} color={STATUS_COLOR.good} />
        <SummaryCard label="Mäßig" value={summary.fair || 0} color={STATUS_COLOR.fair} />
        <SummaryCard label="Kritisch" value={summary.critical || 0} color={STATUS_COLOR.critical} />
        <SummaryCard label="Dringend" value={summary.urgent || 0} color={STATUS_COLOR.urgent} />
      </div>

      {/* Empfehlungen */}
      {empfehlungen.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16, marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>💡 Neue Empfehlungen ({empfehlungen.length})</h3>
          {empfehlungen.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #fde68a' }}>
              <span style={{ flex: 1, fontSize: 14 }}>{r.title}</span>
              {r.vertrag_titel && <span style={{ fontSize: 12, color: '#92400e' }}>{r.vertrag_titel}</span>}
              <button onClick={() => handleEmpfehlung(r.id, 'annehmen')} style={{ fontSize: 12, padding: '4px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', background: '#f59e0b', color: '#fff' }}>Merken</button>
              <button onClick={() => handleEmpfehlung(r.id, 'ablehnen')} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d97706', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Vertragsliste */}
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Alle Verträge ({scores.length})</h2>
      {scores.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          Noch keine Gesundheitsscores berechnet. Tägliche Berechnung erfolgt automatisch.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {scores.map(s => (
            <div key={s.contract_id} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${STATUS_COLOR[s.status] || '#e2e8f0'}44`, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Score-Ring */}
                <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${STATUS_COLOR[s.status]} ${s.total_score * 3.6}deg, #e2e8f0 0deg)` }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: STATUS_COLOR[s.status] }}>
                    {s.total_score}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/vertraege/${s.contract_id}`)}>{s.titel}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: STATUS_COLOR[s.status] + '22', color: STATUS_COLOR[s.status], fontWeight: 600 }}>{STATUS_LABEL[s.status]}</span>
                  </div>
                  {s.anbieter && <div style={{ fontSize: 12, color: '#94a3b8' }}>{s.anbieter}</div>}
                  {s.calculated_at && <div style={{ fontSize: 11, color: '#cbd5e1' }}>Berechnet: {new Date(s.calculated_at).toLocaleString('de-DE')}</div>}
                </div>
                <button onClick={() => handleRecalc(s.contract_id)} disabled={recalcLoading === s.contract_id}
                  style={{ fontSize: 12, padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>
                  {recalcLoading === s.contract_id ? '⟳' : 'Neu berechnen'}
                </button>
              </div>

              {/* Dimensionsbalken */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {DIM_LABELS.map(d => {
                  const val = s[d.key] ?? 0;
                  const pct = Math.round((val / d.max) * 100);
                  return (
                    <div key={d.key} style={{ flex: 1, minWidth: 80 }}>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>{d.label}</div>
                      <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}>
                        <div style={{ height: 6, borderRadius: 3, background: pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444', width: `${pct}%` }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>{val}/{d.max}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 20px', minWidth: 110, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span style={{ fontSize: 26, fontWeight: 800, color }}>{value}</span>
      <span style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</span>
    </div>
  );
}
